/**
 * @module AdapterCard
 * @package @geenius/adapters/react-native
 * @description Renders a native adapter summary card for one adapter domain.
 */

import type { AdapterDomain, AdapterStatusInfo } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
} from "@geenius/adapters";
import type { JSX } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { semanticColors } from "../bridges/TokenBridge";
import { Pressable, StyleSheet, Text, View } from "../lib/native";
import { AdapterStatusBadge } from "./AdapterStatusBadge";

/**
 * Props for a native adapter summary card.
 *
 * @property domain Adapter domain represented by the card.
 * @property onClick Optional compatibility callback invoked when pressed.
 * @property onPress Native press callback invoked when the card is selected.
 * @property status Provider health metadata displayed on the card.
 * @property style Optional native style merged onto the card container.
 * @property testID Optional deterministic selector for tests and Storybook.
 */
export interface AdapterCardProps {
  domain: AdapterDomain;
  onClick?: () => void;
  onPress?: () => void;
  status: AdapterStatusInfo;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * Renders a pressable native summary for one adapter domain.
 *
 * @param props Adapter card props including domain, status, and press handlers.
 * @returns A React Native card rendered through the package runtime bridge.
 * @example
 * ```tsx
 * <AdapterCard domain="db" status={statuses.db} onPress={() => open("db")} />
 * ```
 */
export function AdapterCard({
  domain,
  status,
  onClick,
  onPress,
  style,
  testID,
}: AdapterCardProps): JSX.Element {
  const handlePress = onPress ?? onClick;
  const isInteractive = typeof handlePress === "function";

  return (
    <Pressable
      accessibilityLabel={`${DOMAIN_LABELS[domain]} adapter - ${status.status}`}
      accessibilityRole="button"
      accessibilityState={{ disabled: !isInteractive }}
      disabled={!isInteractive}
      onPress={isInteractive ? handlePress : undefined}
      style={({ pressed }) => [
        styles.card,
        isInteractive && pressed && styles.cardPressed,
        style,
      ]}
      testID={testID ?? `adapter-card-${domain}`}
    >
      <View style={styles.iconBox}>
        <Text style={styles.iconText}>{DOMAIN_ICONS[domain]}</Text>
      </View>
      <View style={styles.body}>
        <View style={styles.header}>
          <Text numberOfLines={1} style={styles.title}>
            {DOMAIN_LABELS[domain]}
          </Text>
          <AdapterStatusBadge status={status.status} />
        </View>
        <Text numberOfLines={2} style={styles.description}>
          {DOMAIN_DESCRIPTIONS[domain]}
        </Text>
        {status.provider !== "none" && (
          <View style={styles.metaRow}>
            <Text style={styles.provider}>{status.provider}</Text>
            {status.latency !== undefined && (
              <Text style={styles.latency}>{status.latency}ms</Text>
            )}
          </View>
        )}
        {status.error && (
          <Text numberOfLines={2} style={styles.error}>
            {status.error}
          </Text>
        )}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  body: {
    flex: 1,
    minWidth: 0,
  },
  card: {
    alignItems: "flex-start",
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.border,
    borderRadius: 10,
    borderWidth: 1,
    flexDirection: "row",
    gap: 14,
    padding: 16,
  },
  cardPressed: {
    backgroundColor: semanticColors.surface.bg,
    transform: [{ scale: 0.99 }],
  },
  description: {
    color: semanticColors.surface.textMuted,
    fontSize: 12,
    lineHeight: 17,
    marginTop: 4,
  },
  error: {
    color: semanticColors.status.error.text,
    fontSize: 11,
    marginTop: 8,
  },
  header: {
    alignItems: "center",
    flexDirection: "row",
    gap: 8,
    justifyContent: "space-between",
  },
  iconBox: {
    alignItems: "center",
    backgroundColor: semanticColors.surface.muted,
    borderColor: semanticColors.surface.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 44,
    justifyContent: "center",
    width: 44,
  },
  iconText: {
    fontSize: 22,
  },
  latency: {
    color: semanticColors.surface.textMuted,
    fontSize: 11,
  },
  metaRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 8,
    marginTop: 10,
  },
  provider: {
    backgroundColor: semanticColors.surface.muted,
    borderRadius: 6,
    color: semanticColors.surface.textSoft,
    fontSize: 11,
    fontWeight: "700",
    overflow: "hidden",
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  title: {
    color: semanticColors.surface.text,
    flex: 1,
    fontSize: 15,
    fontWeight: "800",
  },
});
